/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Helmet from "react-helmet";
import "./clubWebsiteEditor.css";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import {
  CLUB_WEBSITE_PAGE_KEYS,
  EMPTY_CLUB_WEBSITE_BRANDING,
  BRANDING_GALLERY_MAX,
  buildClubWebsitePutBody,
  normalizeClubPages,
  pickWebsiteBrandingFromClub,
} from "../../config/clubWebsitePages";
import ClubBrandingAdvancedFields from "../../components/clubBranding/ClubBrandingAdvancedFields";
import BrandingGalleryField from "../../components/clubBranding/BrandingGalleryField";
import ClubBillingFields from "../../components/clubBranding/ClubBillingFields";
import ClubTableFeeFields from "../../components/clubBranding/ClubTableFeeFields";
import ClubOfflineQrPanel from "../../components/clubShareQr/ClubOfflineQrPanel";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import useAuth from "../../hooks/useAuth";
import { pickManagedClubIdFromUser } from "../../helpers/managedClubId";
import {
  resolveCloudinaryImageSrc,
  uploadFile,
} from "../../helpers/helper.service";
import clubService from "../../store/features/services/clubService";
import {
  normalizeBrandingGalleryForState,
  pickBrandingGalleryFromApiPayload,
  uploadNewGalleryFilesToCloudinary,
} from "../../helpers/brandingGallery";
import {
  EMPTY_CLUB_BILLING,
  pickClubBillingFromClub,
  validateClubBilling,
} from "../../helpers/clubBilling";
import {
  EMPTY_CLUB_TABLE_FEES,
  clubTableFeePutFields,
  pickClubTableFees,
  validateClubTableFees,
} from "../../helpers/tableFeeSettings";
import Constant from "../../util/constant";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomButton from "../../components/customButton/customButton";
import { showToast } from "../../store/features/toast/toastSlice";
import { useDispatch, useSelector } from "react-redux";

const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const frontendUrlPattern = /^https?:\/\/[^\s]+$/i;

const pickClubFrontendUrl = (club) =>
  String(club?.frontend_url ?? club?.frontendUrl ?? "").trim();

/**
 * Admin: `/clubs-management/website/:clubId` — CLUB_MANAGER: `/club-website` (club from JWT).
 * PUT `/club/website/:id` — branding, billing, and legal-style pages (no name/slug/active).
 */
const ClubWebsiteEditorContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { clubId: paramClubId } = useParams();
  const { isClubManager, isAdmin, managedClubId } = useAuth();
  const role = window.localStorage.getItem("role");
  const loginResponse = useSelector((state) => state.auth?.loginResponse);
  const clubIdFromPersistedLogin = useMemo(() => {
    if (
      !loginResponse ||
      typeof loginResponse !== "object" ||
      Array.isArray(loginResponse)
    ) {
      return null;
    }
    return pickManagedClubIdFromUser(loginResponse.user);
  }, [loginResponse]);

  const isAdminRoute =
    (role === Constant.ROLES.ADMIN || isAdmin) && Boolean(paramClubId);
  const resolvedClubId = isAdminRoute
    ? paramClubId
    : managedClubId || clubIdFromPersistedLogin;
  const pageShellClass = "club-staff-page-shell";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clubName, setClubName] = useState("");
  const [clubSlug, setClubSlug] = useState("");
  const [branding, setBranding] = useState(() => ({
    ...EMPTY_CLUB_WEBSITE_BRANDING,
  }));
  const [brandingGallery, setBrandingGallery] = useState([]);
  const [billing, setBilling] = useState(() => ({ ...EMPTY_CLUB_BILLING }));
  const [billingErrors, setBillingErrors] = useState({});
  const [tableFees, setTableFees] = useState(() => ({ ...EMPTY_CLUB_TABLE_FEES }));
  const [tableFeeErrors, setTableFeeErrors] = useState({});
  const [pagesForm, setPagesForm] = useState(() => normalizeClubPages());
  const [logoUploading, setLogoUploading] = useState(false);
  const [frontendUrl, setFrontendUrl] = useState("");
  const [frontendUrlError, setFrontendUrlError] = useState("");
  const logoFileRef = useRef(null);
  const frontendUrlBaselineRef = useRef("");
  /** Snapshot after load (and after successful save) — used so empty `instagramurl` only clears when the user removed a stored value. */
  const brandingBaselineRef = useRef(null);
  const brandingGalleryBaselineRef = useRef([]);
  const cloudinaryBase = getCloudinaryMediaBaseUrl();

  const setB = (key, value) =>
    setBranding((prev) => ({ ...prev, [key]: value }));

  const setBillingField = (key, value) =>
    setBilling((prev) => ({ ...prev, [key]: value }));

  const onBillingFieldBlur = (key) => {
    if (key !== "gstin" && key !== "pan") return;
    setBilling((prev) => {
      const raw = prev[key];
      if (raw == null || raw === "") return prev;
      const upper = String(raw).trim().toUpperCase();
      return upper === raw ? prev : { ...prev, [key]: upper };
    });
    setBillingErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!resolvedClubId) {
      setLoading(false);
      return;
    }
    let cancel = false;
    setLoading(true);
    clubService
      .getClubAdmin(resolvedClubId)
      .then((res) => {
        if (cancel) return;
        const raw = res?.data ?? res;
        if (!raw) return;
        setClubName(raw.name || "");
        setClubSlug(raw.slug || "");
        const loadedFrontendUrl = pickClubFrontendUrl(raw);
        frontendUrlBaselineRef.current = loadedFrontendUrl;
        setFrontendUrl(loadedFrontendUrl);
        setFrontendUrlError("");
        const loadedBranding = pickWebsiteBrandingFromClub(raw);
        const loadedGallery = normalizeBrandingGalleryForState(
          pickBrandingGalleryFromApiPayload(raw)
        );
        brandingBaselineRef.current = loadedBranding;
        brandingGalleryBaselineRef.current = loadedGallery;
        setBranding(loadedBranding);
        setBrandingGallery(loadedGallery);
        setBilling(pickClubBillingFromClub(raw));
        setBillingErrors({});
        setTableFees(pickClubTableFees(raw));
        setTableFeeErrors({});
        setPagesForm(normalizeClubPages(raw.pages));
      })
      .catch((err) => {
        dispatch(
          showToast({
            show: false,
            message: typeof err === "string" ? err : t("CLUB_WEBSITE_LOAD_ERROR"),
            type: "error",
          })
        );
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [resolvedClubId, dispatch, t]);

  const onBackgroundImageUpload = async (fieldKey, file) => {
    if (!file?.type?.startsWith("image/")) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_LOGO_INVALID_TYPE"),
          type: "error",
        })
      );
      return;
    }
    try {
      const url = await uploadFile(file);
      setB(fieldKey, url);
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_BRAND_BG_IMAGE_READY"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            (typeof err?.message === "string" && err.message) ||
            t("CLUB_LOGO_UPLOAD_FAILED"),
          type: "error",
        })
      );
    }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file?.type?.startsWith("image/")) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_LOGO_INVALID_TYPE"),
          type: "error",
        })
      );
      return;
    }
    setLogoUploading(true);
    try {
      const url = await uploadFile(file);
      setB("logo", url);
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_LOGO_READY"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            (typeof err?.message === "string" && err.message) ||
            t("CLUB_LOGO_UPLOAD_FAILED"),
          type: "error",
        })
      );
    } finally {
      setLogoUploading(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!resolvedClubId) return;
    const nextTableFeeErrors = isAdmin
      ? validateClubTableFees(tableFees, t("CLUB_TABLE_FEE_PERCENT_INVALID"))
      : {};
    const nextBillingErrors = validateClubBilling(billing, t);
    if (Object.keys(nextTableFeeErrors).length > 0) {
      setTableFeeErrors(nextTableFeeErrors);
    } else {
      setTableFeeErrors({});
    }
    if (Object.keys(nextBillingErrors).length > 0) {
      setBillingErrors(nextBillingErrors);
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_BILLING_VALIDATION_ERROR"),
          type: "error",
        })
      );
      return;
    }
    if (Object.keys(nextTableFeeErrors).length > 0) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_TABLE_FEE_PERCENT_INVALID"),
          type: "error",
        })
      );
      return;
    }
    setBillingErrors({});
    const trimmedFrontendUrl = String(frontendUrl ?? "").trim();
    if (isAdmin) {
      if (trimmedFrontendUrl && !frontendUrlPattern.test(trimmedFrontendUrl)) {
        setFrontendUrlError(t("CLUB_FRONTEND_URL_INVALID"));
        dispatch(
          showToast({
            show: false,
            message: t("CLUB_FRONTEND_URL_INVALID"),
            type: "error",
          })
        );
        return;
      }
      setFrontendUrlError("");
    }
    if (brandingGallery.length > BRANDING_GALLERY_MAX) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_BRAND_GALLERY_TOO_MANY"),
          type: "error",
        })
      );
      return;
    }
    setSaving(true);
    try {
      let resolvedGallery;
      try {
        resolvedGallery = await uploadNewGalleryFilesToCloudinary(
          brandingGallery
        );
      } catch (err) {
        dispatch(
          showToast({
            show: false,
            message: err?.message || t("CLUB_BRAND_GALLERY_UPLOAD_FAILED"),
            type: "error",
          })
        );
        return;
      }
      const body = buildClubWebsitePutBody(branding, pagesForm, {
        baselineBranding: brandingBaselineRef.current,
        brandingGallery: resolvedGallery,
        billing,
      });
      body.brandinggallery = resolvedGallery;
      if (isAdmin) {
        const frontendPayload = {
          ...clubTableFeePutFields(tableFees),
        };
        if (trimmedFrontendUrl) {
          frontendPayload.frontend_url = trimmedFrontendUrl;
        } else if (frontendUrlBaselineRef.current) {
          frontendPayload.frontend_url = "";
        }
        await clubService.updateClub(resolvedClubId, frontendPayload);
      }
      const res = await clubService.updateClubWebsite(resolvedClubId, body);
      const fresh = await clubService.getClubAdmin(resolvedClubId);
      const freshRaw = fresh?.data ?? fresh;
      const savedFromApi = pickBrandingGalleryFromApiPayload(res);
      const savedFromReload = normalizeBrandingGalleryForState(
        pickBrandingGalleryFromApiPayload(freshRaw)
      );
      const saved =
        savedFromReload.length > 0
          ? savedFromReload
          : savedFromApi.length > 0
            ? savedFromApi
            : normalizeBrandingGalleryForState(resolvedGallery);
      if (
        resolvedGallery.length > 0 &&
        saved.length === 0 &&
        typeof res?.message === "string" &&
        /validation|gallery|blob/i.test(res.message)
      ) {
        dispatch(showToast({ show: false, message: res.message, type: "error" }));
        return;
      }
      brandingBaselineRef.current = { ...branding };
      brandingGalleryBaselineRef.current = saved;
      setBrandingGallery(saved);
      if (freshRaw) {
        setBilling(pickClubBillingFromClub(freshRaw));
        setTableFees(pickClubTableFees(freshRaw));
        setTableFeeErrors({});
        const savedFrontendUrl = pickClubFrontendUrl(freshRaw);
        frontendUrlBaselineRef.current = savedFrontendUrl;
        setFrontendUrl(savedFrontendUrl || trimmedFrontendUrl);
      } else if (isAdmin) {
        frontendUrlBaselineRef.current = trimmedFrontendUrl;
        setFrontendUrl(trimmedFrontendUrl);
      }
      dispatch(
        showToast({
          show: false,
          message:
            (typeof res?.message === "string" && res.message) ||
            t("CLUB_WEBSITE_SAVE_OK"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_WEBSITE_SAVE_ERROR"),
          type: "error",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const galleryNotifyError = (message) => {
    dispatch(
      showToast({
        show: false,
        message,
        type: "error",
      })
    );
  };

  const logo = branding.logo || "";
  const primarycolor = branding.primarycolor || "#000000";
  const secondarycolor = branding.secondarycolor || "#ffffff";

  const logoPreview =
    logo?.trim() &&
    (/^https?:\/\//i.test(logo.trim()) || logo.trim().startsWith("data:"))
      ? logo.trim()
      : logo?.trim()
        ? resolveCloudinaryImageSrc(logo.trim(), cloudinaryBase, "")
        : "";

  const pageTitle = isClubManager
    ? []
    : [
        { title: t("EVENTS"), path: "/events-list" },
        { title: t("CLUB_MANAGEMENT"), path: "/clubs-management" },
      ];

  if (!resolvedClubId) {
    return (
      <div className={`pageContent categoryManagement ${pageShellClass} club-website-editor p-4`}>
        <Helmet>
          <title>{t("CLUB_WEBSITE_EDITOR_TITLE")} | RizzTix</title>
        </Helmet>
        <p className="text-danger mb-0">{t("CLUB_WEBSITE_NO_CLUB")}</p>
      </div>
    );
  }

  return (
    <div className={`pageContent categoryManagement ${pageShellClass} club-website-editor`}>
      <Helmet>
        <title>{t("CLUB_WEBSITE_EDITOR_TITLE")} | RizzTix</title>
      </Helmet>
      <Breadcrumbs
        innerPagetitle={t("CLUB_WEBSITE_EDITOR_TITLE")}
        breadcrumbItem={t("CLUB_WEBSITE_EDITOR_TITLE")}
        pageTitle={pageTitle}
        BreadcrumbInner={pageTitle.length > 0}
        BreadcrumbmainTitle={true}
      />
      <div className="px-3 pb-2">
        <Link
          to={
            isAdminRoute
              ? `/clubs-management/catalog/${resolvedClubId}`
              : "/club-catalog"
          }
        >
          {t("CATALOG_MANAGE_LINK")}
        </Link>
      </div>
      {loading ? (
        <p className="px-3 py-4 text-muted mb-0">{t("LOADING")}</p>
      ) : (
        <Card className="mainCard mainCardWrapper border-0 shadow-sm club-website-editor-card">
          <Card.Body>
            {clubName ? (
              <div className="club-website-section club-website-club-meta">
                <Card.Title as="h2" className="h5 mb-1">
                  {clubName}
                </Card.Title>
                <Card.Subtitle className="text-muted mb-0 fw-normal">
                  {t("CLUB_NAME")}
                </Card.Subtitle>
              </div>
            ) : null}

            <form onSubmit={onSave}>
              {isAdmin ? (
                <section className="club-website-section">
                  <Form.Group className="mb-0" controlId="club-frontend-url">
                    <Form.Label>{t("CLUB_FRONTEND_URL")}</Form.Label>
                    <p className="text-muted small mb-2">
                      {t("CLUB_FRONTEND_URL_HINT")}
                    </p>
                    <Form.Control
                      type="url"
                      inputMode="url"
                      autoComplete="url"
                      placeholder={t("CLUB_FRONTEND_URL_PH")}
                      value={frontendUrl}
                      isInvalid={!!frontendUrlError}
                      disabled={saving}
                      onChange={(e) => {
                        setFrontendUrl(e.target.value);
                        if (frontendUrlError) setFrontendUrlError("");
                      }}
                      onBlur={(e) => setFrontendUrl(e.target.value.trim())}
                    />
                    {frontendUrlError ? (
                      <Form.Control.Feedback type="invalid">
                        {frontendUrlError}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>
                  <div className="mt-4">
                    <ClubTableFeeFields
                      t={t}
                      values={tableFees}
                      errors={tableFeeErrors}
                      disabled={saving}
                      onFieldChange={(key, value) => {
                        setTableFees((prev) => ({ ...prev, [key]: value }));
                        if (tableFeeErrors[key]) {
                          setTableFeeErrors((prev) => {
                            const next = { ...prev };
                            delete next[key];
                            return next;
                          });
                        }
                      }}
                    />
                  </div>
                </section>
              ) : null}

              <section className="club-website-section">
                <Form.Group className="mb-3" controlId="club-website-logo">
                  <Form.Label>{t("CLUB_LOGO")}</Form.Label>
                  <p className="text-muted small mb-2">{t("CLUB_LOGO_UPLOAD_HINT")}</p>
                  <div className="club-website-logo-row">
                    <div className="club-website-logo-thumb">
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt=""
                          className="club-website-logo-img"
                          onError={(ev) => {
                            ev.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="club-website-logo-placeholder">
                          {t("CLUB_BRAND_IMG_PREVIEW_EMPTY")}
                        </span>
                      )}
                    </div>
                    <div className="club-website-logo-actions">
                      <input
                        ref={logoFileRef}
                        type="file"
                        accept="image/*"
                        className="form-control club-website-file-input"
                        disabled={saving || logoUploading}
                        onChange={onLogoFile}
                      />
                      {logoUploading ? (
                        <span className="text-muted small club-website-logo-status">
                          {t("CLUB_LOGO_UPLOADING")}
                        </span>
                      ) : null}
                      {logo?.trim() ? (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm club-website-logo-remove"
                          disabled={saving || logoUploading}
                          onClick={() => {
                            setB("logo", "");
                            if (logoFileRef.current) {
                              logoFileRef.current.value = "";
                            }
                          }}
                        >
                          {t("CLUB_BRAND_BG_CLEAR")}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </Form.Group>

                <div className="row g-3 club-website-colors">
                  <div className="col-12 col-sm-6">
                    <Form.Group controlId="club-primary-color">
                      <Form.Label>{t("CLUB_PRIMARY_COLOR")}</Form.Label>
                      <div className="club-website-color-field">
                        <input
                          type="color"
                          className="form-control form-control-color club-website-color-swatch"
                          value={
                            primarycolor?.match(hexPattern)
                              ? primarycolor
                              : "#000000"
                          }
                          onChange={(e) => setB("primarycolor", e.target.value)}
                          aria-label={t("CLUB_PRIMARY_COLOR")}
                        />
                        <Form.Control
                          className="club-website-color-hex"
                          value={primarycolor}
                          onChange={(e) =>
                            setB("primarycolor", e.target.value)
                          }
                          placeholder="#000000"
                        />
                      </div>
                    </Form.Group>
                  </div>
                  <div className="col-12 col-sm-6">
                    <Form.Group controlId="club-secondary-color">
                      <Form.Label>{t("CLUB_SECONDARY_COLOR")}</Form.Label>
                      <div className="club-website-color-field">
                        <input
                          type="color"
                          className="form-control form-control-color club-website-color-swatch"
                          value={
                            secondarycolor?.match(hexPattern)
                              ? secondarycolor
                              : "#ffffff"
                          }
                          onChange={(e) =>
                            setB("secondarycolor", e.target.value)
                          }
                          aria-label={t("CLUB_SECONDARY_COLOR")}
                        />
                        <Form.Control
                          className="club-website-color-hex"
                          value={secondarycolor}
                          onChange={(e) =>
                            setB("secondarycolor", e.target.value)
                          }
                          placeholder="#ffffff"
                        />
                      </div>
                    </Form.Group>
                  </div>
                </div>
              </section>

              <details className="mb-4 border rounded p-3 club-website-advanced">
                <summary className="fw-semibold">
                  {t("CLUB_BRAND_ADVANCED")}
                </summary>
                <ClubBrandingAdvancedFields
                  t={t}
                  values={branding}
                  primaryFallback={primarycolor}
                  uploadsDisabled={saving}
                  onBackgroundImageUpload={onBackgroundImageUpload}
                  onFieldChange={(key, value) => setB(key, value)}
                />
              </details>

              <section className="club-website-section">
                <BrandingGalleryField
                  t={t}
                  images={brandingGallery}
                  onChange={setBrandingGallery}
                  disabled={saving}
                  onError={galleryNotifyError}
                />
              </section>

              <ClubBillingFields
                t={t}
                clubName={clubName}
                values={billing}
                errors={billingErrors}
                disabled={saving}
                onFieldChange={(key, value) => {
                  setBillingField(key, value);
                  if (billingErrors[key]) {
                    setBillingErrors((prev) => {
                      const next = { ...prev };
                      delete next[key];
                      return next;
                    });
                  }
                }}
                onFieldBlur={onBillingFieldBlur}
              />

              {resolvedClubId ? (
                <ClubOfflineQrPanel
                  clubId={resolvedClubId}
                  clubName={clubName}
                  clubSlug={clubSlug}
                />
              ) : null}

              <section className="club-website-section">
                <h3 className="h5 club-website-section-title">
                  {t("CLUB_WEBSITE_SECTION_PAGES")}
                </h3>
                {CLUB_WEBSITE_PAGE_KEYS.map(({ key, labelKey }) => (
                  <div
                    key={key}
                    className="mb-3 border rounded p-3 club-website-page-block"
                  >
                    <Form.Group className="mb-2">
                      <Form.Label>{t(labelKey)}</Form.Label>
                      <Form.Control
                        placeholder={t("CLUB_PAGE_TITLE_PH")}
                        value={pagesForm[key]?.title ?? ""}
                        onChange={(e) =>
                          setPagesForm((prev) => ({
                            ...prev,
                            [key]: { ...prev[key], title: e.target.value },
                          }))
                        }
                      />
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder={t("CLUB_PAGE_BODY_PH")}
                        value={pagesForm[key]?.body ?? ""}
                        onChange={(e) =>
                          setPagesForm((prev) => ({
                            ...prev,
                            [key]: { ...prev[key], body: e.target.value },
                          }))
                        }
                      />
                    </Form.Group>
                  </div>
                ))}
              </section>

              <div className="club-website-form-actions d-flex flex-wrap gap-2">
                <CustomButton
                  type="submit"
                  className="fillButton club-website-save-btn"
                  loading={saving}
                  disabled={saving}
                >
                  {t("UPDATE")}
                </CustomButton>
              </div>
            </form>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ClubWebsiteEditorContainer;
